import React, { useMemo } from 'react';
import type { Document } from '../App';

// Inline styles for document groups
const styles = {
  card: {
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease',
    marginBottom: '1rem'
  },
  cardHeader: {
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid rgba(0, 0, 0, 0.125)'
  },
  groupTitle: {
    fontWeight: 600,
    color: '#495057'
  }
};

// Cost data interface
interface CostData {
  claudeInputTokens?: number;
  claudeOutputTokens?: number;
  claudeCost?: number;
  llamaInputTokens?: number;
  llamaOutputTokens?: number;
  llamaCost?: number;
  novaInputTokens?: number;
  novaOutputTokens?: number;
  novaCost?: number;
  textractCost?: number;
  totalCost?: number;
  costPerPage?: number;
}

interface DocumentWithCost extends Document {
  costData?: CostData;
  estimatedCostData?: {
    avgEstPages: string;
    bestEstPages: string;
    worstEstPages: string;
    avgCost: string;
    bestCost: string;
    worstCost: string;
    model?: string;
    docTitle?: string;
    costStatus?: 'normal' | 'warning' | 'danger';
  };
}

interface DocumentListProps {
  documents: DocumentWithCost[];
  onSelectDocument: (documentId: string) => void;
}

export const DocumentList: React.FC<DocumentListProps> = ({ 
  documents, 
  onSelectDocument 
}) => {
  // Group documents by prefix (folder)
  const groupedDocuments = useMemo(() => {
    const groups: Record<string, DocumentWithCost[]> = {};
    
    documents.forEach(doc => {
      // Extract prefix (everything before the first '/')
      // Extract prefix (everything before the first '/') or fallback to a pattern match
      const regex = /^([A-Z0-9]+)(?:\/|$)/;
      const prefixMatch = regex.exec(doc.originalFilename);
      const docPrefix = prefixMatch?.[1] || 'Other';

      if (!groups[docPrefix]) {
        groups[docPrefix] = [];
      }
      groups[docPrefix].push(doc);
    });

    // Sort groups alphabetically by prefix
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [documents]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'COMPLETE':
        return <span className="badge bg-success">Complete</span>;
      case 'PROCESSING':
        return <span className="badge bg-primary">Processing</span>;
      case 'FAILED':
        return <span className="badge bg-danger">Failed</span>;
      default:
        return <span className="badge bg-secondary">Unknown</span>;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Format currency for display
  const formatCurrency = (value?: number | null) => {
    if (value === undefined || value === null) return '-';
    return `$${value.toFixed(6)}`;
  };

  return (
    <div className="documents-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Documents</h4>
        <span className="badge bg-secondary">{documents.length} Total</span>
      </div>

      {documents.length === 0 ? (
        <div className="card">
          <div className="card-body p-4 text-center">
            <p className="text-muted">No documents found</p>
          </div>
        </div>
      ) : (
        groupedDocuments.map(([prefix, docs]) => (
          <div className="card" key={prefix} style={styles.card}>
            <div className="card-header d-flex justify-content-between align-items-center" style={styles.cardHeader}>
              <h5 className="mb-0" style={styles.groupTitle}>{prefix}</h5>
              <span className="badge bg-secondary">{docs.length}</span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Document Filename</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Cost</th>
                      <th>Completed At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map((doc) => (
                      <tr key={doc.documentId}>
                        <td>{doc.originalFilename}</td>
                        <td>{getStatusBadge(doc.status)}</td>
                        <td>
                          {doc.status === 'PROCESSING' ? (
                            <div className="progress" style={{ height: '20px' }}>
                              <div 
                                className="progress-bar" 
                                role="progressbar" 
                                style={{ width: `${doc.progress || 0}%` }}
                                aria-valuenow={doc.progress || 0}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              >
                                {doc.progress ? Math.round(doc.progress) : 0}%
                              </div>
                            </div>
                          ) : doc.status === 'COMPLETE' ? (
                            "100%"
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td 
                          className={doc.estimatedCostData?.costStatus === 'danger' 
                            ? "text-white bg-danger fw-bold" 
                            : doc.estimatedCostData?.costStatus === 'warning' 
                              ? "text-dark bg-warning fw-bold" 
                              : doc.costData?.totalCost 
                                ? "text-primary fw-bold" 
                                : ""
                          }
                        >
                          {formatCurrency(doc.costData?.totalCost)}
                          {doc.estimatedCostData && (
                            <small className="d-block text-muted">
                              Est: {formatCurrency(parseFloat(doc.estimatedCostData.avgCost))}
                            </small>
                          )}
                        </td>
                        <td>{formatDate(doc.completedAt)}</td>
                        <td>
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() =>{ onSelectDocument(doc.documentId)} }
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};